import { useState, useEffect, useCallback } from 'react';

/**
 * Auth Utilities & Hook
 * Consolidated authentication utilities for both React and non-React usage
 */

// Storage keys
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'currentUser';

// ============================================================================
// NON-REACTIVE UTILITIES (for use outside React components)
// ============================================================================

/**
 * Get the current access token from localStorage
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Get the current refresh token from localStorage
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Get the current user data from localStorage
 */
export function getCurrentUser(): any | null {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
}

/**
 * Check if the user is currently authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

/**
 * Set authentication tokens and user data
 */
export function setAuthData(
  accessToken: string,
  refreshToken: string,
  user: any
): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Clear all authentication data without redirect
 */
export function clearAuthData(): void {
  // Clear all localStorage items
  localStorage.clear();
}

/**
 * Clear all authentication data and redirect to home
 */
export function logout(): void {
  clearAuthData();
  window.location.href = '/';
}

/**
 * Auth utility constants
 */
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: ACCESS_TOKEN_KEY,
  REFRESH_TOKEN: REFRESH_TOKEN_KEY,
  USER: USER_KEY,
} as const;

// ============================================================================
// REACT HOOK FOR REACTIVE AUTH STATE
// ============================================================================

interface UseAuthReturn {
  // Reactive state
  accessToken: string | null;
  refreshToken: string | null;
  currentUser: any | null;
  authenticated: boolean;

  // Functions (same as non-reactive versions above)
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  getCurrentUser: () => any | null;
  isAuthenticated: () => boolean;
  logout: () => void;
  clearAuthData: () => void;
}

/**
 * React hook that provides reactive authentication state
 * Updates automatically when localStorage changes
 *
 * @returns Reactive auth state and utility functions
 */
export function useAuth(): UseAuthReturn {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean>(false);

  // Update reactive state
  const updateState = useCallback(() => {
    setAccessToken(getAccessToken());
    setRefreshToken(getRefreshToken());
    setCurrentUser(getCurrentUser());
    setAuthenticated(isAuthenticated());
  }, []);

  // Listen for storage changes and update state
  useEffect(() => {
    updateState();

    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === ACCESS_TOKEN_KEY ||
        e.key === REFRESH_TOKEN_KEY ||
        e.key === USER_KEY
      ) {
        updateState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for manual updates within the same tab
    const interval = setInterval(updateState, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [updateState]);

  return {
    // Reactive state
    accessToken,
    refreshToken,
    currentUser,
    authenticated,

    // Utility functions
    getAccessToken,
    getRefreshToken,
    getCurrentUser,
    isAuthenticated,
    logout,
    clearAuthData,
  };
}
